import { useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  clearActivityTimerSnapshot,
  loadActivityTimerSnapshotRecord,
  saveActivityTimerSnapshot,
} from '../../../services/activityTimerPersistence'
import {
  getTimerDurations,
  selectActivityTimerRuntime,
  TIMER_ACTION,
  TIMER_STATUS,
} from '../model/activityTimerSlice'
import { trackFeatureAction } from '../../../services/posthogAnalytics'

export default function useActivityTimer() {
  const dispatch = useDispatch()
  const { timer, ownerId, tabId } = useSelector(selectActivityTimerRuntime)
  const timerRef = useRef(timer)
  const ownerIdRef = useRef(ownerId)

  timerRef.current = timer
  ownerIdRef.current = ownerId

  const isRemote = timer.status !== TIMER_STATUS.IDLE
    && Boolean(ownerId)
    && ownerId !== tabId
  const canMutate = useCallback(() => ownerIdRef.current === tabId, [tabId])

  const prepare = useCallback((options = {}) => {
    const currentRecord = loadActivityTimerSnapshotRecord()
    if (currentRecord && currentRecord.ownerId !== tabId) {
      dispatch({
        type: TIMER_ACTION.SYNC_EXTERNAL,
        state: currentRecord.state,
        ownerId: currentRecord.ownerId,
      })
      return
    }
    dispatch({ type: TIMER_ACTION.PREPARE, ...options })
  }, [dispatch, tabId])
  const updateDraft = useCallback((draft) => {
    if (canMutate()) dispatch({ type: TIMER_ACTION.UPDATE_DRAFT, ...draft })
  }, [canMutate, dispatch])
  const start = useCallback(() => {
    const currentRecord = loadActivityTimerSnapshotRecord()
    if (!canMutate() || (currentRecord && currentRecord.ownerId !== tabId)) return
    const timestamp = Date.now()
    dispatch({ type: TIMER_ACTION.START, now: timestamp })
    trackFeatureAction({ action: 'start', feature: 'timer' })
  }, [canMutate, dispatch, tabId])
  const dispatchAtCurrentTime = useCallback((type, action) => {
    if (!canMutate()) return
    const timestamp = Date.now()
    dispatch({ type, now: timestamp })
    trackFeatureAction({ action, feature: 'timer' })
  }, [canMutate, dispatch])
  const pause = useCallback(
    () => dispatchAtCurrentTime(TIMER_ACTION.PAUSE, 'pause'),
    [dispatchAtCurrentTime],
  )
  const resume = useCallback(
    () => dispatchAtCurrentTime(TIMER_ACTION.RESUME, 'resume'),
    [dispatchAtCurrentTime],
  )
  const stop = useCallback(
    () => dispatchAtCurrentTime(TIMER_ACTION.STOP, 'stop'),
    [dispatchAtCurrentTime],
  )
  const markSaving = useCallback(() => {
    if (canMutate()) dispatch({ type: TIMER_ACTION.SAVE_START })
  }, [canMutate, dispatch])
  const markSaveFailed = useCallback(() => {
    if (!canMutate()) return
    dispatch({ type: TIMER_ACTION.SAVE_FAILURE })
    trackFeatureAction({
      action: 'save',
      feature: 'timer',
      outcome: 'failure',
    })
  }, [canMutate, dispatch])
  const confirmSaveMissing = useCallback(() => {
    if (!canMutate()) return
    dispatch({ type: TIMER_ACTION.CONFIRM_SAVE_MISSING })
    trackFeatureAction({ action: 'confirm_save_missing', feature: 'timer' })
  }, [canMutate, dispatch])
  const finishTimer = useCallback((action, analyticsAction) => {
    if (!canMutate()) return
    clearActivityTimerSnapshot(undefined, { ownerId: tabId })
    dispatch(action)
    trackFeatureAction({ action: analyticsAction, feature: 'timer' })
  }, [canMutate, dispatch, tabId])
  const markSaved = useCallback(
    (logOn) => finishTimer({ type: TIMER_ACTION.SAVE_SUCCESS, logOn }, 'save'),
    [finishTimer],
  )
  const discard = useCallback(
    () => finishTimer({ type: TIMER_ACTION.DISCARD }, 'discard'),
    [finishTimer],
  )
  const takeOwnership = useCallback(() => {
    if (timerRef.current.status === TIMER_STATUS.IDLE) return
    dispatch({ type: TIMER_ACTION.SET_OWNER, ownerId: tabId })
    saveActivityTimerSnapshot(timerRef.current, undefined, { ownerId: tabId })
    trackFeatureAction({ action: 'take_ownership', feature: 'timer' })
  }, [dispatch, tabId])

  return useMemo(() => ({
    ...timer,
    ...getTimerDurations(timer),
    isRemote,
    prepare,
    updateDraft,
    start,
    pause,
    resume,
    stop,
    markSaving,
    markSaveFailed,
    confirmSaveMissing,
    markSaved,
    discard,
    takeOwnership,
  }), [
    timer,
    isRemote,
    prepare,
    updateDraft,
    start,
    pause,
    resume,
    stop,
    markSaving,
    markSaveFailed,
    confirmSaveMissing,
    markSaved,
    discard,
    takeOwnership,
  ])
}
